import { Capacitor } from "@capacitor/core";
import { BleClient } from "@capacitor-community/bluetooth-le";

const BLE_SERVICE_UUID = "12345678-1234-1234-1234-123456789abc";
const BLE_CHAR_UUID = "87654321-4321-4321-4321-abcdef123456";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function isCapacitorNativePlatform() {
    try {
        return Capacitor.isNativePlatform();
    } catch (_) {
        try {
            if (typeof window === "undefined" || !window.Capacitor) return false;
            const platform = window.Capacitor.getPlatform?.() || "web";
            return platform === "android" || platform === "ios";
        } catch (_) {
            return false;
        }
    }
}

export function isWebBluetoothSupported() {
    if (isCapacitorNativePlatform()) {
        return false;
    }
    return typeof navigator !== "undefined" && !!navigator.bluetooth;
}

export function isBluetoothSupported() {
    return isCapacitorNativePlatform() || isWebBluetoothSupported();
}

function resolveBleMode() {
    return isCapacitorNativePlatform() ? "native" : "web";
}

function normalizeBleError(error) {
    const message = String(error?.message || error || "").trim();
    if (!message) {
        return new Error("Не вдалося підключитись до сенсора через Bluetooth");
    }
    if (/user cancelled|requestdevice\(\) chooser|requestDevice cancelled/i.test(message)) {
        return new Error("Підключення скасовано. Оберіть сенсор у списку Bluetooth.");
    }
    if (/no device found/i.test(message)) {
        return new Error(
            "Сенсор не знайдено через Bluetooth. Переконайтесь, що сенсор у режимі provisioning (GasBox-… / AirBox-… / WaterBox-…), живлення увімкнено, Bluetooth на телефоні активний."
        );
    }
    if (/not implemented|plugin is not implemented/i.test(message)) {
        return new Error(
            "Native Bluetooth plugin не підключено в Android-збірці. Перезберіть APK через npm run android:prod."
        );
    }
    if (/permission|denied/i.test(message)) {
        return new Error(
            "Немає дозволу Bluetooth/Location. На Android 12+ (особливо Huawei): Nearby devices + Location ON, і дозвіл «Геодані» для додатку."
        );
    }
    if (/location|gps/i.test(message)) {
        return new Error(
            "Увімкніть Location (Геодані) в шторці Android — на Huawei/Xiaomi BLE-скан без цього часто не бачить GasBox."
        );
    }
    return error instanceof Error ? error : new Error(message);
}

export function buildBleDeviceName(prefix = "GasBox", deviceUid = "") {
    const safePrefix = String(prefix || "GasBox").trim() || "GasBox";
    const safeUid = String(deviceUid || "").trim().replace(/\s+/g, "-");
    return safeUid ? `${safePrefix}-${safeUid}` : safePrefix;
}

function normalizeAdvertisementHex(rawAdvertisement) {
    if (!rawAdvertisement) {
        return "";
    }
    if (typeof rawAdvertisement === "string") {
        return rawAdvertisement.replace(/[^0-9a-fA-F]/g, "").toUpperCase();
    }
    if (rawAdvertisement instanceof DataView) {
        const bytes = new Uint8Array(
            rawAdvertisement.buffer,
            rawAdvertisement.byteOffset,
            rawAdvertisement.byteLength
        );
        return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("").toUpperCase();
    }
    if (rawAdvertisement?.buffer instanceof ArrayBuffer) {
        return Array.from(new Uint8Array(rawAdvertisement.buffer), (byte) => byte.toString(16).padStart(2, "0"))
            .join("")
            .toUpperCase();
    }
    return "";
}

function parseAdvertisementHex(rawAdvertisement) {
    const hex = normalizeAdvertisementHex(rawAdvertisement);
    const names = [];
    const serviceUuids = [];
    if (!hex || hex.length < 4) {
        return { names, serviceUuids };
    }

    let offset = 0;
    while (offset + 2 <= hex.length) {
        const length = parseInt(hex.slice(offset, offset + 2), 16);
        offset += 2;
        if (length === 0) {
            break;
        }
        const fieldEnd = offset + length * 2;
        if (fieldEnd > hex.length || length < 1) {
            break;
        }

        const type = parseInt(hex.slice(offset, offset + 2), 16);
        const dataHex = hex.slice(offset + 2, fieldEnd);
        offset = fieldEnd;

        if ((type === 0x08 || type === 0x09) && dataHex.length >= 2) {
            const bytes = dataHex.match(/.{1,2}/g) || [];
            const name = new TextDecoder("utf-8").decode(
                Uint8Array.from(bytes, (part) => parseInt(part, 16))
            ).replace(/\0/g, "").trim();
            if (name) {
                names.push(name);
            }
            continue;
        }

        if ((type === 0x06 || type === 0x07) && dataHex.length >= 32) {
            for (let uuidOffset = 0; uuidOffset + 32 <= dataHex.length; uuidOffset += 32) {
                const chunk = dataHex.slice(uuidOffset, uuidOffset + 32);
                const bytes = chunk.match(/.{1,2}/g) || [];
                if (bytes.length !== 16) {
                    continue;
                }
                const normalized = [
                    bytes.slice(0, 4).reverse().join(""),
                    bytes.slice(4, 6).reverse().join(""),
                    bytes.slice(6, 8).reverse().join(""),
                    bytes.slice(8, 10).join(""),
                    bytes.slice(10, 16).join(""),
                ].join("-").toLowerCase();
                serviceUuids.push(normalized);
            }
        }
    }

    return { names, serviceUuids };
}

function resolveAdvertisedName(scanResult) {
    const device = scanResult?.device || {};
    const directName = String(scanResult?.localName || device?.name || "").trim();
    if (directName) {
        return directName;
    }

    const parsed = parseAdvertisementHex(scanResult?.rawAdvertisement);
    return String(parsed.names[parsed.names.length - 1] || "").trim();
}

function hasProvisioningService(scanResult) {
    const target = BLE_SERVICE_UUID.toLowerCase();
    const uuids = scanResult?.uuids;
    if (Array.isArray(uuids) && uuids.some((uuid) => String(uuid || "").toLowerCase() === target)) {
        return true;
    }

    const parsed = parseAdvertisementHex(scanResult?.rawAdvertisement);
    return parsed.serviceUuids.some((uuid) => uuid === target);
}

function describeScanCandidate(scanResult) {
    const advertisedName = resolveAdvertisedName(scanResult);
    const address = scanResult?.device?.deviceId || "?";
    if (advertisedName) {
        return `${advertisedName} (${address})`;
    }
    if (hasProvisioningService(scanResult)) {
        return `GasBox provisioning service (${address})`;
    }
    return address;
}

function matchesBleTarget(name, { deviceName, namePrefix, deviceUid }) {
    const normalized = String(name || "").trim();
    if (!normalized) {
        return false;
    }
    const expectedName = String(deviceName || "").trim();
    if (expectedName && normalized.localeCompare(expectedName, undefined, { sensitivity: "accent" }) === 0) {
        return true;
    }
    if (expectedName && normalized.toLowerCase() === expectedName.toLowerCase()) {
        return true;
    }
    const uid = String(deviceUid || "").trim();
    if (uid && normalized.toLowerCase().includes(uid.toLowerCase())) {
        return true;
    }
    // When a concrete serial is known, never match a different GasBox-* by prefix alone.
    if (uid || expectedName) {
        return false;
    }
    const prefix = String(namePrefix || "").trim();
    if (prefix && normalized.toLowerCase().startsWith(prefix.toLowerCase())) {
        return true;
    }
    return false;
}

function stringToDataView(value) {
    const bytes = new TextEncoder().encode(String(value || ""));
    return new DataView(bytes.buffer);
}

function parseNotificationPayload(value) {
    if (!value) return null;

    let buffer = value;
    if (value instanceof DataView) {
        buffer = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
    } else if (value?.buffer instanceof ArrayBuffer) {
        buffer = value.buffer;
    }

    const decoder = new TextDecoder("utf-8");
    const raw = decoder.decode(buffer);
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (_) {
        return { ok: false, error: raw };
    }
}

export class SensorBleProvisioner {
    constructor({ onLog, onStatus } = {}) {
        this.onLog = typeof onLog === "function" ? onLog : () => {};
        this.onStatus = typeof onStatus === "function" ? onStatus : () => {};
        this.mode = resolveBleMode();
        this.device = null;
        this.nativeDeviceId = null;
        this.characteristic = null;
        this.pendingResponse = null;
        this.nativeNotificationHandler = null;
    }

    _log(message) {
        this.onLog(String(message || ""));
    }

    _setStatus(message) {
        this.onStatus(String(message || ""));
    }

    _dispatchNotification(payload) {
        if (!payload) return;
        this._log(JSON.stringify(payload));
        if (this.pendingResponse) {
            this.pendingResponse(payload);
        }
    }

    async _waitForResponse(timeoutMs = 20000) {
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                this.pendingResponse = null;
                reject(new Error("Час очікування відповіді від сенсора минув"));
            }, timeoutMs);

            this.pendingResponse = (payload) => {
                clearTimeout(timer);
                this.pendingResponse = null;
                resolve(payload);
            };
        });
    }

    _handleWebNotification(event) {
        this._dispatchNotification(parseNotificationPayload(event.target.value));
    }

    async connect({ namePrefix = "GasBox", deviceName, deviceUid } = {}) {
        this.mode = resolveBleMode();
        try {
            if (this.mode === "native") {
                await this._connectNative({ namePrefix, deviceName, deviceUid });
                return;
            }
            await this._connectWeb({ namePrefix, deviceName });
        } catch (error) {
            throw normalizeBleError(error);
        }
    }

    async _scanAndPickNativeDevice({ deviceName, namePrefix, deviceUid }, timeoutMs = 35000) {
        return new Promise((resolve, reject) => {
            let settled = false;
            const seen = new Set();
            const serviceMatches = new Map();
            const startedAt = Date.now();

            const finish = async (handler, value) => {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timer);
                clearInterval(progressTimer);
                try {
                    await BleClient.stopLEScan();
                } catch (_) {
                    // ignore
                }
                handler(value);
            };

            const consider = (result) => {
                const address = result?.device?.deviceId;
                if (!address) {
                    return;
                }

                if (!seen.has(address)) {
                    seen.add(address);
                    this._log(`BLE: ${describeScanCandidate(result)}`);
                }

                const advertisedName = resolveAdvertisedName(result);
                if (matchesBleTarget(advertisedName, { deviceName, namePrefix, deviceUid })) {
                    this._log(`Обрано за ім'ям: ${advertisedName || address}`);
                    finish(resolve, result.device);
                    return;
                }

                if (hasProvisioningService(result)) {
                    serviceMatches.set(address, result.device);
                }
            };

            const progressTimer = setInterval(() => {
                const elapsed = Math.round((Date.now() - startedAt) / 1000);
                this._setStatus(`Пошук ${deviceName || namePrefix || "GasBox"}... (${elapsed} с)`);
            }, 1000);

            const timer = setTimeout(() => {
                if (serviceMatches.size === 1) {
                    this._log("Ім'я в advertisement не видно — підключення за service UUID GasBox");
                    finish(resolve, [...serviceMatches.values()][0]);
                    return;
                }
                if (serviceMatches.size > 1) {
                    finish(reject, new Error(
                        `Знайдено ${serviceMatches.size} сенсорів GasBox поруч. Вимкніть зайві або оберіть пристрій у системному списку.`
                    ));
                    return;
                }
                finish(reject, new Error(
                    `Сенсор ${deviceName || namePrefix || ""} не знайдено за ${Math.round(timeoutMs / 1000)} с`
                ));
            }, timeoutMs);

            BleClient.requestLEScan(
                { allowDuplicates: true },
                consider
            ).catch((error) => {
                finish(reject, error);
            });
        });
    }

    async _finishNativeConnection(deviceId) {
        this.nativeDeviceId = deviceId;
        this._setStatus("Підключення до сенсора...");
        await BleClient.connect(deviceId);

        this.nativeNotificationHandler = (value) => {
            this._dispatchNotification(parseNotificationPayload(value));
        };

        await BleClient.startNotifications(
            deviceId,
            BLE_SERVICE_UUID,
            BLE_CHAR_UUID,
            this.nativeNotificationHandler
        );

        this._setStatus("Підключено до сенсора");
    }

    async _connectNative({ namePrefix, deviceName, deviceUid }) {
        this._setStatus("Ініціалізація Bluetooth...");
        // androidNeverForLocation:false — Huawei/EMUI часто вимагають Location навіть на Android 12+.
        await BleClient.initialize({ androidNeverForLocation: false });
        if (typeof BleClient.requestPermissions === "function") {
            try {
                await BleClient.requestPermissions();
            } catch (permError) {
                this._log(`BLE permissions: ${permError?.message || permError}`);
            }
        }

        const enabled = await BleClient.isEnabled();
        if (!enabled) {
            this._setStatus("Увімкніть Bluetooth...");
            await BleClient.requestEnable();
        }

        const expectedName = String(
            deviceName || buildBleDeviceName(namePrefix, deviceUid || "")
        ).trim();
        this._setStatus(`Пошук ${expectedName}...`);
        this._log("Huawei/нові Android: увімкніть Location (Геодані) і Nearby devices.");

        let device = null;
        try {
            device = await this._scanAndPickNativeDevice({
                deviceName: expectedName,
                namePrefix,
                deviceUid,
            });
        } catch (scanError) {
            this._log(`Auto-scan не знайшов сенсор: ${scanError.message}`);
            this._setStatus("Відкриваю системний список Bluetooth...");
            try {
                device = await BleClient.requestDevice({
                    services: [BLE_SERVICE_UUID],
                    optionalServices: [BLE_SERVICE_UUID],
                });
            } catch (_) {
                try {
                    device = await BleClient.requestDevice({
                        namePrefix,
                        optionalServices: [BLE_SERVICE_UUID],
                    });
                } catch (_) {
                    device = await BleClient.requestDevice({
                        optionalServices: [BLE_SERVICE_UUID],
                    });
                }
            }
        }

        if (!device?.deviceId) {
            throw new Error("Сенсор не знайдено. Увімкніть Location і Nearby devices, закрийте nRF Connect.");
        }

        await this._finishNativeConnection(device.deviceId);
    }

    async _connectWeb({ namePrefix, deviceName }) {
        if (!isWebBluetoothSupported()) {
            throw new Error("Bluetooth недоступний. Використайте Android-додаток або Chrome.");
        }

        this._setStatus("Пошук сенсора через Bluetooth...");
        const requestOptions = deviceName
            ? { filters: [{ name: deviceName }], optionalServices: [BLE_SERVICE_UUID] }
            : { filters: [{ namePrefix }], optionalServices: [BLE_SERVICE_UUID] };

        this.device = await navigator.bluetooth.requestDevice(requestOptions);

        this._setStatus("Підключення до сенсора...");
        const server = await this.device.gatt.connect();
        const service = await server.getPrimaryService(BLE_SERVICE_UUID);
        this.characteristic = await service.getCharacteristic(BLE_CHAR_UUID);
        await this.characteristic.startNotifications();
        this.characteristic.addEventListener("characteristicvaluechanged", this._handleWebNotification.bind(this));
        this._setStatus("Підключено до сенсора");
    }

    async disconnect() {
        try {
            if (this.mode === "native" && this.nativeDeviceId) {
                if (this.nativeNotificationHandler) {
                    await BleClient.stopNotifications(
                        this.nativeDeviceId,
                        BLE_SERVICE_UUID,
                        BLE_CHAR_UUID
                    ).catch(() => {});
                }
                await BleClient.disconnect(this.nativeDeviceId).catch(() => {});
            } else if (this.characteristic) {
                this.characteristic.removeEventListener("characteristicvaluechanged", this._handleWebNotification.bind(this));
                if (this.device?.gatt?.connected) {
                    this.device.gatt.disconnect();
                }
            }
        } catch (_) {
            // ignore
        } finally {
            this.device = null;
            this.nativeDeviceId = null;
            this.characteristic = null;
            this.nativeNotificationHandler = null;
            this.pendingResponse = null;
        }
    }

    async sendCommand(command, { timeoutMs = 20000 } = {}) {
        const payload = JSON.stringify(command);
        this._log(`→ ${payload}`);
        const responsePromise = this._waitForResponse(timeoutMs);

        if (this.mode === "native") {
            if (!this.nativeDeviceId) {
                throw new Error("Сенсор не підключено");
            }
            await BleClient.write(
                this.nativeDeviceId,
                BLE_SERVICE_UUID,
                BLE_CHAR_UUID,
                stringToDataView(payload)
            );
        } else {
            if (!this.characteristic) {
                throw new Error("Сенсор не підключено");
            }
            await this.characteristic.writeValue(new TextEncoder().encode(payload));
        }

        const response = await responsePromise;
        if (response?.ok === false) {
            throw new Error(response.error || response.message || "Сенсор повернув помилку");
        }
        return response;
    }

    async getStatus() {
        return this.sendCommand({ cmd: "get_status" });
    }

    async scanWifi() {
        this._setStatus("Сканування Wi-Fi мереж...");
        const response = await this.sendCommand({ cmd: "scan_wifi" }, { timeoutMs: 35000 });
        const raw = Array.isArray(response?.networks) ? response.networks : [];
        const networks = raw
            .map((item) => ({
                ssid: String(item?.ssid || item?.s || "").trim(),
                rssi: Number(item?.rssi ?? item?.r ?? 0),
                secure: item?.secure === true || item?.e === 1 || item?.e === true,
            }))
            .filter((item) => item.ssid);
        return { ...response, networks };
    }

    async setWifi({ ssid, password }) {
        return this.sendCommand({ cmd: "set_wifi", ssid, password });
    }

    async setWifiAlt({ ssid, password, clear = false } = {}) {
        if (clear || !String(ssid || "").trim()) {
            return this.sendCommand({ cmd: "set_wifi_alt", clear: true, ssid: "", password: "" });
        }
        return this.sendCommand({
            cmd: "set_wifi_alt",
            ssid: String(ssid || "").trim(),
            password: password || "",
        });
    }

    async setTelegram({ enabled, botToken, chatId }) {
        return this.sendCommand({
            cmd: "set_telegram",
            enabled: !!enabled,
            bot_token: botToken || "",
            chat_id: chatId || "",
        });
    }

    async setViber({ enabled, authToken, receiverId }) {
        return this.sendCommand({
            cmd: "set_viber",
            enabled: !!enabled,
            auth_token: authToken || "",
            receiver_id: receiverId || "",
        });
    }

    async setBackend({
        enabled,
        alertUrl,
        heartbeatUrl,
        bearerToken,
        deviceId,
        networkId,
        locationId,
    }) {
        return this.sendCommand({
            cmd: "set_backend",
            enabled: !!enabled,
            alert_url: alertUrl,
            heartbeat_url: heartbeatUrl,
            bearer_token: bearerToken,
            device_id: deviceId,
            network_id: networkId ?? 0,
            location_id: locationId ?? 0,
        });
    }

    async setThresholds(thresholds = {}) {
        const eco2Warning = Number(thresholds.eco2_warning) || Number(thresholds.mq135_warning) || 0;
        const eco2Alarm = Number(thresholds.eco2_alarm) || Number(thresholds.mq135_alarm) || 0;
        return this.sendCommand({
            cmd: "set_thresholds",
            mq2_warning: Number(thresholds.mq2_warning) || 0,
            mq2_alarm: Number(thresholds.mq2_alarm) || 0,
            mq135_warning: eco2Warning,
            mq135_alarm: eco2Alarm,
            eco2_warning: eco2Warning,
            eco2_alarm: eco2Alarm,
            tvoc_warning: Number(thresholds.tvoc_warning) || 0,
            tvoc_alarm: Number(thresholds.tvoc_alarm) || 0,
            heartbeat_interval_sec: Number(thresholds.heartbeat_interval_sec) || Number(thresholds.check_interval_sec) || 900,
            check_interval_sec: Number(thresholds.check_interval_sec) || Number(thresholds.heartbeat_interval_sec) || 900,
            buzzer_enabled: thresholds.buzzer_enabled !== false,
        });
    }

    async commit() {
        this._setStatus("Збереження конфігурації на сенсорі...");
        const response = await this.sendCommand({ cmd: "commit" }, { timeoutMs: 45000 });
        await sleep(500);
        return response;
    }
}

export function buildIotUrl(apiBase, path) {
    const base = String(apiBase || "").trim().replace(/\/+$/, "");
    const suffix = String(path || "").trim();
    if (!base || !suffix) return "";
    return `${base}${suffix.startsWith("/") ? suffix : `/${suffix}`}`;
}
